/**
 * Smooth scroll HolyDougz — inertie légère avec Lenis, sur le conteneur qui défile
 * réellement (au-delà de 990 px c'est `.page-wrapper`, pas la fenêtre).
 *
 * Lenis pilote `scrollTop` en natif : les IntersectionObserver d'Horizon (en-tête collé,
 * pagination), le footer en rideau, le hero collé et scroll-container.js continuent de
 * fonctionner. On ne l'active pas : sous 990 px (tactile), si le visiteur préfère moins
 * d'animations, en économie de données, ou dans l'éditeur de thème.
 */
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const DESKTOP = window.matchMedia('(min-width: 990px)');
const COARSE = window.matchMedia('(pointer: coarse)');

/** @type {import('./hd-lenis.js').default | null} */
let lenis = null;
/** @type {ResizeObserver | null} */
let sizer = null;

function allowed() {
  const conn = /** @type {any} */ (navigator).connection;
  if (REDUCED.matches || !DESKTOP.matches || COARSE.matches) return false;
  if (conn?.saveData || /(^|[^\d])[23]g$/.test(conn?.effectiveType ?? '')) return false;
  if (/** @type {any} */ (window).Shopify?.designMode) return false;
  return true;
}

async function start() {
  if (lenis || !allowed()) return;
  const wrapper = document.querySelector('.page-wrapper');
  const content = document.getElementById('MainContent');
  if (!(wrapper instanceof HTMLElement) || !(content instanceof HTMLElement)) return;

  const { default: Lenis } = await import(new URL('./hd-lenis.js', import.meta.url).href);
  if (lenis || !allowed()) return;

  lenis = new Lenis({
    wrapper,
    content,
    eventsTarget: wrapper,
    lerp: 0.1,
    smoothWheel: true,
    syncTouch: false,
    autoRaf: true,
    autoResize: true,
    naiveDimensions: true,
    allowNestedScroll: true,
    stopInertiaOnNavigate: true,
  });

  // Lenis n'observe que `content` : le footer (rideau) et l'en-tête comptent aussi.
  sizer = new ResizeObserver(() => lenis?.resize());
  for (const el of wrapper.children) sizer.observe(el);

  /** @type {any} */ (window).__hdLenis = lenis;
  syncLock();
}

function stop() {
  sizer?.disconnect();
  sizer = null;
  lenis?.destroy();
  lenis = null;
  /** @type {any} */ (window).__hdLenis = null;
}

/**
 * Gèle l'inertie quand un tiroir ou une modale est ouvert : sinon la page continue de
 * défiler derrière le voile (Lenis écrit `scrollTop`, qu'`overflow: hidden` n'arrête pas).
 * Deux signaux : l'attribut `scroll-lock` posé sur <html> par utilities.js (menu, recherche,
 * ajout rapide) et l'ouverture d'un `<dialog>` de tiroir, que le panier n'annonce pas autrement.
 */
function syncLock() {
  if (!lenis) return;
  const locked =
    document.documentElement.hasAttribute('scroll-lock') ||
    document.querySelector('dialog.theme-drawer__dialog[open], dialog.dialog-modal[open]') !== null;
  if (locked) lenis.stop();
  else lenis.start();
}

new MutationObserver(syncLock).observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['scroll-lock'],
});

new MutationObserver(syncLock).observe(document.body, {
  attributes: true,
  attributeFilter: ['open'],
  subtree: true,
});

// Ancres de la même page (/#faq…) : défilement Lenis en respectant scroll-margin.
document.addEventListener('click', (event) => {
  if (!lenis) return;
  const link = /** @type {HTMLElement | null} */ (event.target)?.closest?.('a[href*="#"]');
  if (!(link instanceof HTMLAnchorElement)) return;
  const url = new URL(link.href, location.href);
  if (url.pathname !== location.pathname || url.hash.length < 2) return;
  const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
  if (!target) return;
  event.preventDefault();
  const margin = parseFloat(getComputedStyle(target).scrollMarginBlockStart) || 0;
  lenis.scrollTo(target, { offset: -margin });
  history.pushState(null, '', url.hash);
});

DESKTOP.addEventListener('change', () => (DESKTOP.matches ? start() : stop()));
REDUCED.addEventListener('change', () => (REDUCED.matches ? stop() : start()));
window.addEventListener('pageshow', () => lenis?.resize());

if (document.readyState === 'complete') start();
else window.addEventListener('load', () => start(), { once: true });
